/**
 * Admin/PresetManager.tsx
 *
 * Save and load named "presets" — each preset is a labelled collection of
 * monday.com board IDs.  Selecting a preset tells the rest of the app which
 * boards to surface in the My Work, Monitor, and Analytics widgets.
 *
 * Storage key: "preset_configs"  →  PresetConfig[]
 * Active preset key: "active_preset"  →  string (preset id)
 */

import { useState, useEffect, useCallback } from 'react';
import { storageGet, storageSet } from '@core/api/client';
import { useBoards } from '@core/hooks/useMondayData';
import type { PresetConfig } from '@core/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateId(): string {
  return `preset_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ManagerState = 'loading' | 'idle' | 'saving' | 'error';

// ─── Component ────────────────────────────────────────────────────────────────

export function PresetManager() {
  const [presets, setPresets] = useState<PresetConfig[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [managerState, setManagerState] = useState<ManagerState>('loading');
  const [statusMessage, setStatusMessage] = useState('');

  // Form state for creating a new preset
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Fetch all available boards for the board selector
  const { data: allBoards, isLoading: boardsLoading } = useBoards(100);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadPresets = useCallback(async () => {
    setManagerState('loading');
    try {
      const [stored, active] = await Promise.all([
        storageGet<PresetConfig[]>('preset_configs'),
        storageGet<string>('active_preset'),
      ]);
      setPresets(stored ?? []);
      setActivePresetId(active ?? null);
      setManagerState('idle');
    } catch (err) {
      setStatusMessage(`Failed to load presets: ${(err as Error).message}`);
      setManagerState('error');
    }
  }, []);

  useEffect(() => {
    void loadPresets();
  }, [loadPresets]);

  // ── Activate preset ─────────────────────────────────────────────────────────

  async function activatePreset(id: string) {
    setManagerState('saving');
    try {
      await storageSet('active_preset', id);
      setActivePresetId(id);
      setStatusMessage('Preset activated.');
      setManagerState('idle');
    } catch (err) {
      setStatusMessage(`Could not activate preset: ${(err as Error).message}`);
      setManagerState('error');
    }
  }

  // ── Create preset ────────────────────────────────────────────────────────────

  async function createPreset() {
    if (!newName.trim()) return;
    const preset: PresetConfig = {
      id: generateId(),
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      boardIds: [...selectedBoardIds],
      createdAt: new Date().toISOString(),
    };
    const updated = [...presets, preset];
    setManagerState('saving');
    try {
      await storageSet('preset_configs', updated);
      setPresets(updated);
      setNewName('');
      setNewDescription('');
      setSelectedBoardIds(new Set());
      setShowCreateForm(false);
      setStatusMessage(`Preset "${preset.name}" created.`);
      setManagerState('idle');
    } catch (err) {
      setStatusMessage(`Failed to save preset: ${(err as Error).message}`);
      setManagerState('error');
    }
  }

  // ── Delete preset ────────────────────────────────────────────────────────────

  async function deletePreset(id: string) {
    const updated = presets.filter((p) => p.id !== id);
    setManagerState('saving');
    try {
      await storageSet('preset_configs', updated);
      if (activePresetId === id) {
        await storageSet('active_preset', '');
        setActivePresetId(null);
      }
      setPresets(updated);
      setStatusMessage('Preset deleted.');
      setManagerState('idle');
    } catch (err) {
      setStatusMessage(`Delete failed: ${(err as Error).message}`);
      setManagerState('error');
    }
  }

  // ── Board toggle helper ──────────────────────────────────────────────────────

  function toggleBoard(boardId: string) {
    setSelectedBoardIds((prev) => {
      const next = new Set(prev);
      if (next.has(boardId)) {
        next.delete(boardId);
      } else {
        next.add(boardId);
      }
      return next;
    });
  }

  // ── Derived ──────────────────────────────────────────────────────────────────

  const isDisabled = managerState === 'saving' || managerState === 'loading';
  const activePreset = presets.find((p) => p.id === activePresetId);

  // ── UI ───────────────────────────────────────────────────────────────────────

  return (
    <div className="cos-admin-card">
      <div className="cos-admin-card__header">
        <h3 className="cos-admin-heading">Preset Manager</h3>
        <div className="cos-admin-card__actions">
          <button
            type="button"
            className="cos-btn cos-btn--secondary cos-btn--sm"
            onClick={() => void loadPresets()}
            disabled={isDisabled}
          >
            ↺ Refresh
          </button>
          <button
            type="button"
            className="cos-btn cos-btn--primary cos-btn--sm"
            onClick={() => setShowCreateForm((v) => !v)}
            disabled={isDisabled}
          >
            {showCreateForm ? '✕ Cancel' : '+ New Preset'}
          </button>
        </div>
      </div>

      <p className="cos-admin-muted">
        Presets are named groups of monday.com boards. Activating a preset tells all widgets
        which boards to load by default.
      </p>

      {/* Status banners */}
      {managerState === 'loading' && (
        <div className="cos-admin-banner cos-admin-banner--info">
          <span className="cos-spinner" aria-hidden="true" /> Loading presets…
        </div>
      )}
      {managerState === 'saving' && (
        <div className="cos-admin-banner cos-admin-banner--info">
          <span className="cos-spinner" aria-hidden="true" /> Saving…
        </div>
      )}
      {managerState === 'error' && (
        <div className="cos-admin-banner cos-admin-banner--error">✗ {statusMessage}</div>
      )}
      {managerState === 'idle' && statusMessage && (
        <div className="cos-admin-banner cos-admin-banner--success">✓ {statusMessage}</div>
      )}

      {/* Active preset callout */}
      {activePreset && (
        <div className="cos-admin-active-preset">
          <span className="cos-admin-active-dot" aria-hidden="true" />
          <span>
            Active preset: <strong>{activePreset.name}</strong>
            {activePreset.description && (
              <span className="cos-admin-muted"> — {activePreset.description}</span>
            )}
          </span>
        </div>
      )}

      {/* ── Create form ──────────────────────────────────────────────────────── */}
      {showCreateForm && (
        <section className="cos-admin-section cos-admin-create-form">
          <h4 className="cos-admin-subheading">New Preset</h4>

          <label className="cos-admin-label">
            Name
            <input
              type="text"
              className="cos-admin-input"
              placeholder="e.g. Q2 Boards"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={60}
              autoFocus
            />
          </label>

          <label className="cos-admin-label">
            Description (optional)
            <input
              type="text"
              className="cos-admin-input"
              placeholder="Short note about this preset"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              maxLength={120}
            />
          </label>

          <fieldset className="cos-admin-fieldset">
            <legend className="cos-admin-label">Select Boards</legend>
            {boardsLoading && (
              <p className="cos-admin-muted">
                <span className="cos-spinner" aria-hidden="true" /> Loading boards…
              </p>
            )}
            {!boardsLoading && (!allBoards || allBoards.length === 0) && (
              <p className="cos-admin-muted">No boards found. Check your Worker connection.</p>
            )}
            <div className="cos-admin-board-list">
              {allBoards?.map((board) => (
                <label key={board.id} className="cos-admin-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedBoardIds.has(String(board.id))}
                    onChange={() => toggleBoard(String(board.id))}
                  />
                  <span>{board.name}</span>
                  <span className="cos-admin-muted cos-admin-badge">ID: {board.id}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="cos-admin-form-actions">
            <button
              type="button"
              className="cos-btn cos-btn--primary cos-btn--sm"
              disabled={!newName.trim() || selectedBoardIds.size === 0 || isDisabled}
              onClick={() => void createPreset()}
            >
              Create Preset
            </button>
            <button
              type="button"
              className="cos-btn cos-btn--secondary cos-btn--sm"
              onClick={() => {
                setShowCreateForm(false);
                setNewName('');
                setNewDescription('');
                setSelectedBoardIds(new Set());
              }}
            >
              Cancel
            </button>
            {selectedBoardIds.size === 0 && newName.trim() && (
              <span className="cos-admin-muted cos-admin-validation">
                Select at least one board to continue.
              </span>
            )}
          </div>
        </section>
      )}

      {/* ── Preset list ──────────────────────────────────────────────────────── */}
      {managerState !== 'loading' && presets.length === 0 && !showCreateForm && (
        <div className="cos-admin-empty">
          <p>No presets saved yet.</p>
          <p className="cos-admin-muted">
            Click <strong>+ New Preset</strong> to create your first one.
          </p>
        </div>
      )}

      {presets.map((preset) => {
        const isActive = preset.id === activePresetId;
        return (
          <section key={preset.id} className={`cos-admin-preset${isActive ? ' cos-admin-preset--active' : ''}`}>
            <div className="cos-admin-preset__meta">
              {isActive && (
                <span className="cos-admin-active-dot cos-admin-active-dot--sm" aria-label="Active" />
              )}
              <div>
                <span className="cos-admin-subheading">{preset.name}</span>
                {preset.description && (
                  <p className="cos-admin-muted">{preset.description}</p>
                )}
                <p className="cos-admin-muted">
                  {preset.boardIds.length} board{preset.boardIds.length !== 1 ? 's' : ''} ·
                  Created {formatDate(preset.createdAt)}
                </p>
              </div>
            </div>
            <div className="cos-admin-preset__actions">
              {!isActive && (
                <button
                  type="button"
                  className="cos-btn cos-btn--primary cos-btn--sm"
                  disabled={isDisabled}
                  onClick={() => void activatePreset(preset.id)}
                >
                  Activate
                </button>
              )}
              {isActive && (
                <span className="cos-admin-badge cos-admin-badge--active">Active</span>
              )}
              <button
                type="button"
                className="cos-btn cos-btn--danger cos-btn--sm"
                disabled={isDisabled}
                onClick={() => void deletePreset(preset.id)}
              >
                Delete
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );
}
